import { HttpClient } from '@angular/common/http'
import { DestroyRef, inject, Injectable } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Store } from '@ngxs/store'
import { catchError, debounceTime, distinctUntilChanged, filter, of, switchMap, take } from 'rxjs'
import { ENV_TOKEN } from '../app.config'
import { EmployeeService } from './employee.service'

/**
 * Welche Teile der Dashboard-States dem Mitarbeiter an jeden Arbeitsplatz folgen: seine
 * KPI-Kacheln (eigene wie entfernte) und seine Spaltenauswahl.
 *
 * **Der Filter steht bewusst NICHT drin** (Entscheidung Tim 2026-08-10). Er ist Arbeitsstand von
 * gerade eben, kein Setup — wer morgens am anderen Rechner anfaengt, erwartet die volle Liste und
 * nicht den Filter von gestern Abend. Er bleibt im localStorage und damit pro Browser.
 *
 * Die Schluessel sind die echten Feldnamen der State-Modelle; ein Tippfehler faellt still durch
 * (der Wert existiert dann einfach nicht), deshalb sind sie unten gegen die Modelle typisiert.
 */
const SYNCED_KEYS = {
  phmDashboard: ['phmColumns', 'phmKpiCards', 'phmDismissedPresets'],
  himiDashboard: ['himiColumns', 'himiKpiCards', 'himiDismissedPresets'],
  patientDashboard: ['patientColumns', 'patientKpiCards', 'patientDismissedPresets'],
  // Die Verkaufsliste hat keine Spaltenauswahl (feste Spalten), also nur die Kacheln.
  selbstzahlerDashboard: ['selbstzahlerKpiCards', 'selbstzahlerDismissedPresets'],
  ekvDashboard: ['ekvColumns'],
} as const satisfies Record<string, readonly string[]>

/** Wie lange nach der letzten Aenderung gewartet wird, bevor gespeichert wird. */
const SAVE_DEBOUNCE_MS = 1500

type StateSlice = Record<string, unknown>
type SettingsBlob = Record<string, StateSlice>

/**
 * Haelt die persoenlichen Dashboard-Einstellungen zwischen Browser und Mitarbeiter-Datensatz
 * synchron. Vorher lagen sie ausschliesslich im localStorage — also pro Rechner verschieden und
 * nach einem Cache-Leeren weg.
 *
 * Ablauf: einmal beim Start aus `employees/currentlyloggedin` uebernehmen (die Antwort haelt der
 * `shareReplay` des EmployeeService ohnehin schon vor, es kostet also keinen zusaetzlichen Aufruf),
 * danach jede Aenderung gebuendelt zurueckschreiben.
 *
 * **Jeder Fehler ist hier folgenlos:** faellt das Laden oder Speichern aus — altes Backend ohne den
 * Endpunkt, kaputter Blob, Netzfehler — bleibt der localStorage die Quelle und die App verhaelt
 * sich wie vorher. Eine Fehlermeldung waere hier falsch: der Nutzer hat nichts getan, was
 * fehlschlagen konnte, und kann nichts daran richten.
 */
@Injectable({ providedIn: 'root' })
export class DashboardSettingsSyncService {
  private readonly store = inject(Store)
  private readonly http = inject(HttpClient)
  private readonly employeeService = inject(EmployeeService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly apiUrl = inject(ENV_TOKEN).api.leistungserbringerUrl

  /**
   * Bis der Server-Stand uebernommen ist, wird NICHT gespeichert. Sonst schriebe der erste
   * Snapshot (= der lokale Stand dieses Browsers) den Server-Stand platt, bevor er ueberhaupt
   * angekommen ist — aus "folgt mir an jeden Rechner" wuerde "der zuletzt geoeffnete Browser
   * gewinnt".
   */
  private hydrated = false

  start(): void {
    this.employeeService.currentlyLoggedInEmployee$
      .pipe(
        take(1),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((employee) => {
        this.hydrate(employee?.dashboardSettings)
        this.watchForChanges()
      })
  }

  private hydrate(raw: string | undefined): void {
    const blob = this.parse(raw)
    if (blob) {
      // reset() statt einer Action je State: Hydrieren ist genau das Ersetzen des Snapshots, und
      // vier zusaetzliche Actions haetten hier keinen eigenen fachlichen Inhalt. Es wird auf den
      // aktuellen Snapshot GEMERGT, damit nicht synchronisierte Teile (v.a. der Filter) bleiben.
      const snapshot = this.store.snapshot()
      const merged = { ...snapshot }
      for (const [stateName, keys] of Object.entries(SYNCED_KEYS)) {
        const incoming = blob[stateName]
        if (!incoming || typeof incoming !== 'object') {
          continue
        }
        const patch: StateSlice = {}
        for (const key of keys) {
          if (incoming[key] !== undefined) {
            patch[key] = incoming[key]
          }
        }
        merged[stateName] = { ...snapshot[stateName], ...patch }
      }
      this.store.reset(merged)
    }
    this.hydrated = true
  }

  private parse(raw: string | undefined): SettingsBlob | null {
    if (!raw) {
      return null
    }
    try {
      const parsed: unknown = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as SettingsBlob) : null
    } catch {
      // Kaputter Blob: lieber die lokalen Einstellungen behalten als die App daran aufhaengen.
      return null
    }
  }

  private watchForChanges(): void {
    this.store
      .select((state: Record<string, StateSlice>) => JSON.stringify(this.collect(state)))
      .pipe(
        filter(() => this.hydrated),
        debounceTime(SAVE_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((payload) =>
          this.http
            .put(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/dashboard-settings`, { dashboardSettings: payload })
            .pipe(catchError(() => of(null))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  private collect(state: Record<string, StateSlice>): SettingsBlob {
    const blob: SettingsBlob = {}
    for (const [stateName, keys] of Object.entries(SYNCED_KEYS)) {
      const slice = state[stateName]
      if (!slice) {
        continue
      }
      const picked: StateSlice = {}
      for (const key of keys) {
        if (slice[key] !== undefined) {
          picked[key] = slice[key]
        }
      }
      blob[stateName] = picked
    }
    return blob
  }
}
